/**
 * usePushNotification — hook para gestionar suscripciones Web Push (VAPID)
 * Compatible con iOS Safari 16.4+ (requiere modo standalone/PWA)
 */
import { useState, useEffect, useCallback } from "react";
import apiClient from "./api";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  return new Uint8Array([...rawData].map((c) => c.charCodeAt(0)));
}

/** Detect iOS device */
function detectIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

/** Detect standalone (PWA) mode — works for iOS and Android */
function detectStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export function usePushNotification(userId) {
  const [state, setState] = useState({
    supported: false,
    permission: "default",
    subscribed: false,
    loading: false,
    error: null,
    isIOS: false,
    isStandalone: false,
  });

  // Check initial state
  useEffect(() => {
    const isIOS = detectIOS();
    const isStandalone = detectStandalone();
    // On iOS, push requires standalone mode AND iOS 16.4+
    const supported = "serviceWorker" in navigator && "PushManager" in window;
    const permission = supported ? Notification.permission : "denied";
    setState((s) => ({ ...s, supported, permission, isIOS, isStandalone }));

    if (supported) {
      navigator.serviceWorker.ready.then((reg) => {
        reg.pushManager.getSubscription().then((sub) => {
          setState((s) => ({ ...s, subscribed: !!sub }));
        });
      });
    }
  }, []);

  const subscribe = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      // iOS requires standalone mode — block early with a helpful error
      const isIOS = detectIOS();
      const isStandalone = detectStandalone();
      if (isIOS && !isStandalone) {
        throw new Error(
          "iOS_NOT_STANDALONE: Para activar notificaciones en iPhone, primero agrega la app a tu pantalla de inicio desde Safari."
        );
      }

      // 1. Request notification permission (must come from user gesture)
      const perm = await Notification.requestPermission();
      setState((s) => ({ ...s, permission: perm }));
      if (perm !== "granted") throw new Error("Permiso denegado por el usuario");

      // 2. Get VAPID public key
      const { data } = await apiClient.get("/push/vapid-key");
      const vapidKey = data.vapid_public_key;
      if (!vapidKey) throw new Error("Clave VAPID no disponible");

      // 3. Subscribe via PushManager
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });

      // 4. Send subscription to backend
      const subJSON = sub.toJSON();
      await apiClient.post("/push/subscribe", {
        user_id: userId,
        endpoint: subJSON.endpoint,
        keys: subJSON.keys,
      });

      setState((s) => ({ ...s, subscribed: true, loading: false }));
      return true;
    } catch (err) {
      setState((s) => ({ ...s, loading: false, error: err.message }));
      return false;
    }
  }, [userId]);

  const unsubscribe = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const { endpoint } = sub.toJSON();
        await apiClient.post("/push/unsubscribe", { endpoint });
        await sub.unsubscribe();
      }
      setState((s) => ({ ...s, subscribed: false, loading: false }));
    } catch (err) {
      setState((s) => ({ ...s, loading: false, error: err.message }));
    }
  }, []);

  return { ...state, subscribe, unsubscribe };
}
